import { IItinerary } from "@thekilroygroup/shared-ui";
import {
  ICabinClassMap as ISkyscannerCabinClassMap,
  IPageTypes,
  TSkyscannerStage,
  TripTypeMap as ISkyscannerTripTypeMap,
} from "./interfaces.skyscanner";

/**
 *
 * @param itinerary Shared utils
 * @param travellerType
 * @returns
 */

export interface ITravellerAmount {
  total: number;
  adults: number;
  children: number;
  infants: number;
}

export const findTravellerAmount = (itinerary: IItinerary) => {
  let travellers: ITravellerAmount = {
    total: 0,
    adults: 0,
    children: 0,
    infants: 0,
  };

  if (!itinerary) {
    return travellers;
  }

  itinerary.fareBreakdowns.map((fareBreakdown) => {
    if (
      fareBreakdown.passengerType === "ADT" ||
      fareBreakdown.passengerType === "STU" ||
      fareBreakdown.passengerType === "YTH"
    ) {
      travellers.adults = fareBreakdown.passengerQuantity;
    } else if (
      fareBreakdown.passengerType === "CNN" ||
      fareBreakdown.passengerType === "CHD"
    ) {
      travellers.children = fareBreakdown.passengerQuantity;
    } else if (fareBreakdown.passengerType === "INF") {
      travellers.infants = fareBreakdown.passengerQuantity;
    }
    travellers.total =
      travellers.adults + travellers.children + travellers.infants;
  });
  return travellers;
};

/**
 * Skyscanner utils
 */
export const createLegsData = (itinerary?: IItinerary) => {
  if (!itinerary) return "";
  const legs: string[] = itinerary.flights.map((flight) => {
    const leg: string[] = [];

    flight.flightSegments.forEach((flightSegment, index) => {
      leg.push(flightSegment.departureAirport);
      if (index === flight.flightSegments.length - 1) {
        leg.push(flightSegment.arrivalAirport);
      }
    });

    flight.flightSegments.forEach((flightSegment) => {
      leg.push(flightSegment.departureDateTime, flightSegment.arrivalDateTime);
    });
    return leg.join(",");
  });

  return legs.join("|");
};

export const cabinClassMap: ISkyscannerCabinClassMap = {
  ECONOMY: "economy",
  PREMIUM_ECONOMY: "premium-economy",
  FIRST: "first",
  PREMIUM_FIRST: "first",
  BUSINESS: "business",
  PREMIUM_BUSINESS: "business",
};

export const skyscannerTripTypeMap: ISkyscannerTripTypeMap = {
  ONE_WAY: "one-way",
  RETURN: "return",
  OPEN_JAW: "multiple-destination",
  MULTI_DEST: "multiple-destination",
};

export const skyscannerPageTypeMap: {
  [key in IPageTypes]: TSkyscannerStage;
} = {
  "/": "home-page",
  "/search-results": "dayview-page",
  "/calendar": "dayview-page",
  "/checkout": "booking-page",
  "/confirmation": "confirmation",
};

/**
 * Google flight utils
 */

export const googleFlightsFindItems = (itinerary: any) => {
  return itinerary.flights
    .map((flight: any) => {
      return flight.flightSegments.map((segment: any) => {
        return {
          travel_class: googleFlightsCabinClassMap[segment.cabinClass],
          fare_product: googleFlightsCabinClassMap[segment.cabinClass],
          booking_code: segment.bookingClass,
          flight_number: `${segment.marketingAirline}${segment.flightNumber}`,
          origin: segment.departureAirport,
          destination: segment.arrivalAirport,
          start_date: segment.departureDateTime.substring(0, 10),
        };
      });
    })
    .flat();
};

export type TGoogleFlightsTripType = "OneWay" | "RoundTrip" | "Other";

export interface IGoogleFlightsTripTypeMap {
  ONE_WAY: TGoogleFlightsTripType;
  RETURN: TGoogleFlightsTripType;
  OPEN_JAW: TGoogleFlightsTripType;
  MULTI_DEST: TGoogleFlightsTripType;
}

export const googleFlightsTripTypeMap: IGoogleFlightsTripTypeMap = {
  ONE_WAY: "OneWay",
  RETURN: "RoundTrip",
  OPEN_JAW: "RoundTrip",
  MULTI_DEST: "RoundTrip",
};

export interface IGooglePassengerCounts {
  total: number;
  adult: number;
  child: number;
  infant_in_seat: number;
  infant_in_lap: number;
}

export interface IGoogleFlightItem {
  travel_class: "Economy" | "PremiumEconomy";
  fare_product: string;
  booking_code: string;
  flight_number: string;
  origin: string;
  destination: string;
  start_date: string;
}

export interface IGoogleTrackingData {
  send_to: string;
  trip_type: string;
  value: string;
  currency: string;
  passengers: IGooglePassengerCounts;
  items: IGoogleFlightItem[];
}

export type IGoogleFlightsCabinType =
  | "Economy"
  | "Business"
  | "PremiumBusiness"
  | "PremiumEconomy"
  | "First"
  | "PremiumFirst";

export interface IGoogleFlightsCabinClassMap {
  [key: string]: IGoogleFlightsCabinType;
}

export const googleFlightsCabinClassMap: IGoogleFlightsCabinClassMap = {
  ECONOMY: "Economy",
  PREMIUM_ECONOMY: "PremiumEconomy",
  BUSINESS: "Business",
  PREMIUM_BUSINESS: "PremiumBusiness",
  FIRST: "First",
  PREMIUM_FIRST: "PremiumFirst",
};

export const getUserDevice = () => {
  const userAgent = navigator.userAgent;
  if (/mobile/i.test(userAgent)) {
    return "mobile";
  } else if (/tablet/i.test(userAgent)) {
    return "tablet";
  } else {
    return "desktop";
  }
};
